import { useEffect, useId, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { playNotifyChime } from "@/lib/notifySound";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";

/**
 * Tickets that still need the current actor's attention:
 * - customer view: support replied after the customer's last read
 * - agent view (forAgent): customer wrote after the agent's last read
 */
export function useSupportUnread(forAgent = false, withSound = false) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const prevRef = useRef<Set<string> | null>(null);
  // Several components mount this hook at once; each needs its own realtime
  // channel topic, otherwise the second one reuses an already-subscribed
  // channel and Supabase throws "cannot add postgres_changes callbacks".
  const instanceId = useId();
  // perf_large_data: poll every 2 minutes and, for customers, listen only
  // to tickets they opened (agents still need every ticket).
  const perfLargeData = useFeatureFlag("perf_large_data");


  const query = useQuery({
    queryKey: ["support-unread", user?.id, forAgent],
    enabled: !!user?.id,
    refetchInterval: perfLargeData ? 120_000 : 30_000,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc("support_unread_ticket_ids", {
        p_for_agent: forAgent,
      });
      if (error) return [] as string[];
      return ((data as { ticket_id: string }[]) || []).map((r) => r.ticket_id);
    },
  });

  const ids: string[] = query.data || [];

  // Sound: only when a ticket becomes unread that was not unread before.
  useEffect(() => {
    if (!withSound || query.isLoading) return;
    const next = new Set(ids);
    const prev = prevRef.current;
    prevRef.current = next;
    if (!prev) return; // first snapshot after load: no sound
    for (const id of next) {
      if (!prev.has(id)) {
        playNotifyChime();
        break;
      }
    }
  }, [ids.join(","), withSound, query.isLoading]);

  // Live refresh when any visible ticket gets a new message.
  useEffect(() => {
    if (!user?.id) return;
    const topic = `support_unread_${forAgent ? "agent" : "customer"}_${user.id}_${instanceId.replace(/[^\w]/g, "")}`;
    const ch = supabase
      .channel(topic)
      .on("postgres_changes", {
        event: "*", schema: "public", table: "support_tickets",
        ...(perfLargeData && !forAgent ? { filter: `created_by=eq.${user.id}` } : {}),
      } as any, () => {
        query.refetch();
      })
      .subscribe();
    return () => {
      supabase.removeChannel(ch);
    };
  }, [user?.id, forAgent, instanceId, perfLargeData]);


  return {
    unreadIds: new Set(ids),
    unreadCount: ids.length,
    refetch: query.refetch,
    invalidate: () => queryClient.invalidateQueries({ queryKey: ["support-unread"] }),
  };
}

export async function markSupportTicketRead(ticketId: string) {
  try {
    await (supabase as any).rpc("mark_support_ticket_read", { p_ticket_id: ticketId });
  } catch {
    /* non-blocking */
  }
}
